import React, { useCallback, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material'

import { VideoWidgetData } from '../../../types/gotypes'
import { MediaLayoutControls, mediaAlignToFlex, MediaAlign } from './MediaLayoutControls'

// Converts a YouTube or Vimeo share URL to an embed URL.
const parseEmbedUrl = (url: string): string | null => {
  const trimmed = url.trim()

  // YouTube: watch?v=, youtu.be/, shorts/
  const yt = trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  )
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`

  // Vimeo
  const vimeo = trimmed.match(/vimeo\.com\/(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`

  // Already a recognised embed URL
  if (
    trimmed.startsWith('https://www.youtube.com/embed/') ||
    trimmed.startsWith('https://player.vimeo.com/video/')
  ) {
    return trimmed
  }

  return null
}

// Display-friendly form of an embed URL for showing in the text field
function embedToDisplayUrl(embedUrl: string): string {
  const yt = embedUrl.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/)
  if (yt) return `https://www.youtube.com/watch?v=${yt[1]}`

  const vimeo = embedUrl.match(/player\.vimeo\.com\/video\/(\d+)/)
  if (vimeo) return `https://vimeo.com/${vimeo[1]}`

  return embedUrl
}

export const ASPECT_RATIOS = ['16:9', '9:16', '4:3', '1:1'] as const
export type AspectRatio = (typeof ASPECT_RATIOS)[number]

const aspectRatioToCss = (ratio: string | undefined): string => {
  switch (ratio) {
    case '9:16':
      return '9/16'
    case '4:3':
      return '4/3'
    case '1:1':
      return '1/1'
    default:
      return '16/9'
  }
}

interface EmbedOptions {
  autoplay?: boolean
  mute?: boolean
  loop?: boolean
  start_time?: number
  hide_controls?: boolean
}

/**
 * Applies embed-native playback options to a base embed URL, using each
 * provider's own query params. Providers other than YouTube/Vimeo (or a
 * blank URL) degrade gracefully — the base URL is returned unmodified. #1071.
 */
export function buildEmbedSrc(baseEmbedUrl: string, opts: EmbedOptions): string {
  if (!baseEmbedUrl) return ''

  const isYouTube = baseEmbedUrl.includes('youtube.com/embed/')
  const isVimeo = baseEmbedUrl.includes('player.vimeo.com/video/')

  if (isYouTube) {
    const idMatch = baseEmbedUrl.match(/embed\/([a-zA-Z0-9_-]{11})/)
    const videoId = idMatch ? idMatch[1] : ''
    const params = new URLSearchParams()
    if (opts.autoplay) params.set('autoplay', '1')
    // YouTube requires mute=1 for autoplay to actually play in most browsers;
    // an explicit mute choice is respected independently of autoplay too.
    if (opts.mute || opts.autoplay) params.set('mute', '1')
    if (opts.loop && videoId) {
      params.set('loop', '1')
      params.set('playlist', videoId) // required by YouTube to loop a single video
    }
    if (opts.start_time && opts.start_time > 0) params.set('start', String(Math.floor(opts.start_time)))
    if (opts.hide_controls) {
      params.set('controls', '0')
      params.set('rel', '0')
    }
    const qs = params.toString()
    return qs ? `${baseEmbedUrl}?${qs}` : baseEmbedUrl
  }

  if (isVimeo) {
    const params = new URLSearchParams()
    if (opts.autoplay) params.set('autoplay', '1')
    if (opts.mute || opts.autoplay) params.set('muted', '1')
    if (opts.loop) params.set('loop', '1')
    if (opts.hide_controls) {
      params.set('controls', '0')
      params.set('byline', '0')
      params.set('title', '0')
      params.set('portrait', '0')
    }
    const qs = params.toString()
    const base = qs ? `${baseEmbedUrl}?${qs}` : baseEmbedUrl
    // Vimeo's start-time is a URL fragment, so it must come after the query string.
    if (opts.start_time && opts.start_time > 0) {
      return `${base}#t=${Math.floor(opts.start_time)}s`
    }
    return base
  }

  // Unrecognised provider — no known param support, return unmodified.
  return baseEmbedUrl
}

interface VideoEmbedFrameProps {
  data: VideoWidgetData
}

/** Shared render for the editor's live preview and the final widget renderer,
 *  so the two never drift (mirrors the existing near-identical pattern). */
const VideoEmbedFrame: React.FC<VideoEmbedFrameProps> = ({ data }) => {
  if (!data.embed_url) return null

  const src = buildEmbedSrc(data.embed_url, {
    autoplay: data.autoplay,
    mute: data.mute,
    loop: data.loop,
    start_time: data.start_time,
    hide_controls: data.hide_controls,
  })
  const maxWidth = data.max_width && data.max_width > 0 && data.max_width < 100 ? `${data.max_width}%` : '100%'
  const justify = mediaAlignToFlex(data.align)

  return (
    <Box sx={{ display: 'flex', width: '100%', justifyContent: justify }}>
      <Box
        component="iframe"
        src={src}
        sx={{
          width: '100%',
          maxWidth,
          aspectRatio: aspectRatioToCss(data.aspect_ratio),
          border: 'none',
          borderRadius: 1,
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Box>
  )
}

interface VideoWidgetEditorProps {
  customer: string
  job: string
  dashboardId: string
  data: VideoWidgetData | undefined
  onDataChange: (data: VideoWidgetData) => void
}

export const VideoWidgetEditor: React.FC<VideoWidgetEditorProps> = ({ data, onDataChange }) => {
  const [inputValue, setInputValue] = useState(
    data?.embed_url ? embedToDisplayUrl(data.embed_url) : '',
  )
  const [error, setError] = useState<string | null>(null)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setInputValue(val)
      setError(null)

      if (!val.trim()) {
        onDataChange({ ...data, job: '', file_path: '', embed_url: '' })
        return
      }

      const embedUrl = parseEmbedUrl(val)
      if (embedUrl) {
        onDataChange({ ...data, job: '', file_path: '', embed_url: embedUrl })
      }
    },
    [data, onDataChange],
  )

  const handleBlur = useCallback(() => {
    if (inputValue.trim() && !parseEmbedUrl(inputValue)) {
      setError('Unrecognised URL — paste a YouTube or Vimeo link.')
    }
  }, [inputValue])

  const update = useCallback(
    (partial: Partial<VideoWidgetData>) => {
      if (data) onDataChange({ ...data, ...partial })
    },
    [data, onDataChange],
  )

  const embedUrl = data?.embed_url || null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="YouTube or Vimeo URL"
        size="small"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="https://www.youtube.com/watch?v=..."
        fullWidth
        error={Boolean(error)}
        helperText={error ?? undefined}
      />

      {embedUrl && <VideoEmbedFrame data={data as VideoWidgetData} />}

      {embedUrl && (
        <>
          <Box>
            <Typography variant="overline" color="text.secondary">Aspect Ratio</Typography>
            <ToggleButtonGroup
              value={data?.aspect_ratio || '16:9'}
              exclusive
              onChange={(_e, val: string | null) => val && update({ aspect_ratio: val })}
              size="small"
            >
              {ASPECT_RATIOS.map((ratio) => (
                <ToggleButton key={ratio} value={ratio}>{ratio}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <MediaLayoutControls
            align={(data?.align as MediaAlign) || 'center'}
            onAlignChange={(align) => update({ align })}
            maxWidth={data?.max_width || 100}
            onMaxWidthChange={(max_width) => update({ max_width })}
          />

          <Box>
            <Typography variant="overline" color="text.secondary">Playback</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={Boolean(data?.autoplay)} onChange={(e) => update({ autoplay: e.target.checked })} />}
                label="Autoplay"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={Boolean(data?.mute)} onChange={(e) => update({ mute: e.target.checked })} />}
                label="Mute"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={Boolean(data?.loop)} onChange={(e) => update({ loop: e.target.checked })} />}
                label="Loop"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={Boolean(data?.hide_controls)} onChange={(e) => update({ hide_controls: e.target.checked })} />}
                label="Hide controls"
              />
            </Box>
          </Box>

          <TextField
            label="Start time (seconds, optional)"
            size="small"
            type="number"
            value={data?.start_time ?? ''}
            onChange={(e) => update({ start_time: e.target.value ? Number(e.target.value) : undefined })}
            inputProps={{ min: 0 }}
            sx={{ maxWidth: 240 }}
          />
        </>
      )}
    </Box>
  )
}

interface VideoWidgetRendererProps {
  data: VideoWidgetData
  customer: string
}

export const VideoWidgetRenderer: React.FC<VideoWidgetRendererProps> = ({ data }) => {
  if (!data.embed_url) {
    return (
      <Typography variant="body2" color="text.secondary">
        No video configured
      </Typography>
    )
  }

  return <VideoEmbedFrame data={data} />
}
